import { ApolloError, useApolloClient } from '@apollo/client';

import { useCreateDocumentsWithRelationshipsMutation } from '@/modules/files/hooks/graphql/UseCreateDocuments.generated';
import {
  CreateDocumentInput,
  CreateDocumentWithEntityRelationshipsInput,
  CreateRelationshipWithEntityInput,
  DocumentType,
  EntityDocumentRelationshipRelationshipType,
} from '@/types/schema';

interface CreateDocumentOptionsEntity {
  entityId: string;
}

interface CreateDocumentAndRelationshipsOptions {
  householdId: string;
  fileId: string;
  fileName: string;
  documentType: DocumentType;
  entities: CreateDocumentOptionsEntity[];
}

type UseCreateDocumentsAndRelationshipsReturn = [
  (options: CreateDocumentAndRelationshipsOptions[]) => Promise<{
    documentIds: string[];
  }>,
  { loading: boolean; error: ApolloError | undefined },
];

export function useCreateDocumentsAndRelationships(): UseCreateDocumentsAndRelationshipsReturn {
  const client = useApolloClient();

  const [createDocumentsWithRelationships, { loading, error }] =
    useCreateDocumentsWithRelationshipsMutation();

  async function handleCreateDocuments(
    documents: CreateDocumentAndRelationshipsOptions[]
  ) {
    const input: CreateDocumentWithEntityRelationshipsInput[] = documents.map(
      (doc) => {
        // Preserve the document's legacy owner edge for existing consumers.
        const documentOwnerEntityId =
          doc.entities.length > 0 ? doc.entities[0]?.entityId : undefined;

        const documentInput: CreateDocumentInput = {
          entityID: documentOwnerEntityId,
          householdID: doc.householdId,
          type: doc.documentType,
          fileID: doc.fileId,
          name: doc.fileName,
        };

        const relationships: CreateRelationshipWithEntityInput[] =
          doc.entities.map((entity) => ({
            householdID: doc.householdId,
            entityID: entity.entityId,
            relationshipType:
              EntityDocumentRelationshipRelationshipType.Mentioned,
          }));

        return {
          document: documentInput,
          relationships,
        };
      }
    );

    const { data } = await createDocumentsWithRelationships({
      variables: {
        input,
      },
      onCompleted: async () => {
        await client.refetchQueries({
          updateCache(cache) {
            cache.evict({ fieldName: 'documents' });
            cache.gc();
          },
        });
      },
    });

    return {
      documentIds:
        data?.createDocumentsWithEntityRelationships.map((doc) => doc.id) ?? [],
    };
  }

  return [handleCreateDocuments, { loading, error }];
}
